import jsPDF from "jspdf";
import Chart from "chart.js/auto";

interface FinancialItem {
  category: string;
  value: string | number;
}

interface ChartData {
  labels: string[];
  data: number[];
}

interface MultiDataset {
  labels: string[];
  datasets: Array<{ label: string; data: number[] }>;
}

export interface GrantCostDetail {
  programName: string;
  creditAmount: string;
  fullAmountDetails?: string;
  appliesTo: string;
  eligibility: string;
  validUntil: string;
  actionLink: string;
  description: string;
  _id?: string;
}

export interface TaxBenefitsDetails {
  applyTaxBenefits?: boolean;
  businessTaxRate?: number;
  section179Limit?: number;
  bonusDepreciationPct?: number;
  qualifyingSpend?: number;
  taxSavings?: number;
  upfrontAfterTax?: number;
}

export interface ReportDetails {
  reportName: string;
  netProfit: number;
  address: string;
  financialTable?: FinancialItem[];
  breakEvenCashFlow?: ChartData;
  revenueCost?: MultiDataset;
  co2Savings?: MultiDataset;
  firstName?: string;
  lastName?: string;
  email?: string;
  utilityDetails?: {
    state: string;
    zipCode: string;
    utilityName: string;
    utilityId: string;
    scheduleId: string;
    totalCost: number;
  };
  energyCosts?: {
    energy: number[];
    energyTime: number[];
    incrementalEnergy: number[];
    demand: number[];
    demandTime: number[];
    incrementalDemand: number[];
    serviceCharge: number[];
    otherCharges: number[];
    total: number[];
  };
  grantCostDetails?: GrantCostDetail[];
  excludeIncentives?: boolean;
  taxBenefits?: TaxBenefitsDetails;
}

export interface WhiteLabelConfig {
  brandLogo: string;
  brandColor: string;
  textColor: string;
  bgColor: string;
  domainURL: string;
  termsOfServiceURL: string;
  privacyPolicyURL: string;
}

const CompanyLogo = require("../images/png/RightSite Rectangle Black copy.png");

const PDF_IMAGE_QUALITY = 0.72;
const CHART_EXPORT_WIDTH = 600;
const CHART_EXPORT_HEIGHT = 320;
const MAP_EXPORT_WIDTH = 640;
const MAP_EXPORT_HEIGHT = 320;

const loadImageElement = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = (error) => reject(error);
    image.src = src;
  });

const compressImageDataUrl = async (
  dataUrl: string,
  {
    maxWidth,
    maxHeight,
    quality = PDF_IMAGE_QUALITY,
    format = "image/jpeg",
    backgroundColor = "#ffffff",
  }: {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    format?: "image/jpeg" | "image/png";
    backgroundColor?: string;
  } = {}
): Promise<string> => {
  if (!dataUrl || !dataUrl.startsWith("data:image")) {
    return dataUrl;
  }

  try {
    const image = await loadImageElement(dataUrl);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return dataUrl;
    }

    const widthScale = maxWidth ? maxWidth / image.width : 1;
    const heightScale = maxHeight ? maxHeight / image.height : 1;
    const scale = Math.min(1, widthScale, heightScale);

    const targetWidth = Math.max(1, Math.floor(image.width * scale));
    const targetHeight = Math.max(1, Math.floor(image.height * scale));

    canvas.width = targetWidth;
    canvas.height = targetHeight;

    if (format === "image/jpeg") {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, targetWidth, targetHeight);
    }

    ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

    return canvas.toDataURL(format, quality);
  } catch (error) {
    console.error("Image compression failed:", error);
    return dataUrl;
  }
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return { r, g, b };
};

const createStaticMapImage = async (
  address: string,
  retries = 2
): Promise<string | null> => {
  const isValidAddress =
    address && address.trim() && address.toLowerCase() !== "n/a";
  const validAddress = isValidAddress
    ? address.trim()
    : "981 US Route 1, Edison, NJ, USA";
  const encodedAddress = encodeURIComponent(validAddress);

  let center = encodedAddress;
  let marker = encodedAddress;
  let zoom = 10;

  if (window.google && window.google.maps) {
    try {
      const geocoder = new window.google.maps.Geocoder();
      const { results } = await new Promise<{
        results: google.maps.GeocoderResult[];
      }>((resolve, reject) => {
        geocoder.geocode({ address: validAddress }, (results, status) => {
          if (status === window.google.maps.GeocoderStatus.OK && results) {
            resolve({ results });
          } else {
            reject(new Error(`Geocode status: ${status}`));
          }
        });
      });

      if (results[0]) {
        const pos = results[0].geometry.location;
        center = `${pos.lat()},${pos.lng()}`;
        marker = `${pos.lat()},${pos.lng()}`;
        zoom = 15;
      }
    } catch (error) {
      console.error("Geocoding failed:", error);
    }
  }

  const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${center}&zoom=${zoom}&size=${MAP_EXPORT_WIDTH}x${MAP_EXPORT_HEIGHT}&scale=1&maptype=roadmap&markers=color:red%7C${marker}&key=AIzaSyDZsmG9zzmlEZLY3BpPYeJMfUphIobw-sM`;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      console.log(
        `Fetching static map for address: ${validAddress} (Attempt ${attempt}/${retries})`
      );
      const response = await fetch(staticMapUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      if (!blob.type.startsWith("image/")) {
        throw new Error("Received non-image response from Google Maps API");
      }

      const imageData = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          if (result.startsWith("data:image")) {
            resolve(result);
          } else {
            reject(new Error("Invalid image data URL"));
          }
        };
        reader.onerror = () =>
          reject(new Error("Failed to read map image blob"));
        reader.readAsDataURL(blob);
      });

      // Log if fallback address was used
      if (!isValidAddress) {
        console.warn(
          `Using fallback address: ${validAddress} due to invalid input address: ${address}`
        );
      }

      return compressImageDataUrl(imageData, {
        maxWidth: MAP_EXPORT_WIDTH,
        maxHeight: MAP_EXPORT_HEIGHT,
        quality: PDF_IMAGE_QUALITY,
        format: "image/jpeg",
      });
    } catch (error) {
      console.error(
        `Failed to create static map for address: ${validAddress} (Attempt ${attempt}/${retries}):`,
        error
      );
      if (attempt === retries) {
        console.error("All retries failed for static map image");
        return null;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }
  return null;
};

const generateCharts = async (details: ReportDetails) => {
  const charts: { [key: string]: Chart | undefined } = {};

  // Chart background plugin
  const chartBackgroundPlugin = {
    id: "chartBackground",
    beforeDraw: (chart: any) => {
      const ctx = chart.ctx as CanvasRenderingContext2D;
      ctx.save();
      ctx.fillStyle = "#fafcfb";
      ctx.fillRect(0, 0, chart.width, chart.height);
      ctx.restore();
    },
  };

  const lineShadowPlugin = {
    id: "lineShadow",
    beforeDatasetsDraw: (chart: any, _args: any, pluginOptions: any) => {
      const ctx = chart.ctx as CanvasRenderingContext2D;
      ctx.save();
      ctx.shadowColor = pluginOptions?.color || "rgba(0,0,0,0.25)";
      ctx.shadowBlur = pluginOptions?.blur ?? 5;
      ctx.shadowOffsetX = pluginOptions?.offsetX ?? 5;
      ctx.shadowOffsetY = pluginOptions?.offsetY ?? 5;
    },
    beforeDatasetDraw: (chart: any, args: any, pluginOptions: any) => {
      const ctx = chart.ctx as CanvasRenderingContext2D;
      const dataset = chart.data?.datasets?.[args.index] as any;
      const baseAlpha = pluginOptions?.alpha ?? 10;

      const toRgba = (color: any, alpha: number) => {
        if (typeof color !== "string") return `rgba(0,0,0,${alpha})`;
        if (color.startsWith("#")) {
          let r = 0,
            g = 0,
            b = 0;
          if (color.length === 4) {
            r = parseInt(color[1] + color[1], 16);
            g = parseInt(color[2] + color[2], 16);
            b = parseInt(color[3] + color[3], 16);
          } else if (color.length === 7) {
            r = parseInt(color.slice(1, 3), 16);
            g = parseInt(color.slice(3, 5), 16);
            b = parseInt(color.slice(5, 7), 16);
          }
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
        const rgbMatch = color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i);
        if (rgbMatch) {
          const [, r, g, b] = rgbMatch;
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
        const rgbaMatch = color.match(
          /^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([0-9]*\.?[0-9]+)\)$/i
        );
        if (rgbaMatch) {
          const [, r, g, b] = rgbaMatch;
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
        return color;
      };

      let borderColor = dataset?.borderColor;
      if (Array.isArray(borderColor) && borderColor.length > 0) {
        borderColor = borderColor[0];
      }
      ctx.shadowColor = toRgba(borderColor, baseAlpha);
    },
    afterDatasetsDraw: (chart: any) => {
      const ctx = chart.ctx as CanvasRenderingContext2D;
      ctx.restore();
    },
  };

  // Break Even Chart
  if (details.breakEvenCashFlow) {
    const breakEvenCanvas = document.createElement("canvas");
    breakEvenCanvas.width = CHART_EXPORT_WIDTH;
    breakEvenCanvas.height = CHART_EXPORT_HEIGHT;
    const breakEvenCtx = breakEvenCanvas.getContext("2d");
    if (breakEvenCtx) {
      charts.breakEven = new Chart(breakEvenCtx, {
        type: "line",
        data: {
          labels: details.breakEvenCashFlow.labels,
          datasets: [
            {
              label: "Cumulative Cash Flow",
              data: details.breakEvenCashFlow.data,
              borderColor: "#4b5b5d",
              backgroundColor: "rgba(75, 91, 93, 0.2)",
              fill: true,
              tension: 0.5,
              borderWidth: 2,
            },
          ],
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          animation: false,
          scales: {
            y: {
              // beginAtZero: true,
              suggestedMin: Math.min(...details.breakEvenCashFlow.data) * 1.1,
              suggestedMax: Math.max(...details.breakEvenCashFlow.data) * 1.1,

              title: { display: true, text: "Cash ($)", font: { size: 14 } },
              grid: { color: "#e0e0e0" },
              ticks: { font: { size: 12 } },
            },
            x: {
              title: { display: true, text: "Years", font: { size: 14 } },
              grid: { color: "#e0e0e0" },
              ticks: { font: { size: 12 } },
            },
          },
          plugins: {
            legend: {
              position: "bottom",
              labels: { font: { size: 14 }, padding: 20 },
            },
          },
        },
        plugins: [chartBackgroundPlugin],
      });
    }
  }

  // Revenue Cost Chart
  if (details.revenueCost) {
    const revenueCanvas = document.createElement("canvas");
    revenueCanvas.width = CHART_EXPORT_WIDTH;
    revenueCanvas.height = CHART_EXPORT_HEIGHT;
    const revenueCtx = revenueCanvas.getContext("2d");
    if (revenueCtx) {
      charts.revenueCost = new Chart(revenueCtx, {
        type: "line",
        data: {
          labels: details.revenueCost.labels,
          datasets: details.revenueCost.datasets.map((dataset, index) => ({
            ...dataset,
            borderColor:
              [
                "#4B5B5D",
                "#4682B4",
                "#B22222",
                "#A9A9A9",
                "#3CB371",
                "#FFA500",
                "#D3D3D3",
              ][index] || "#4B5B5D",
            backgroundColor: "rgba(75, 91, 93, 0.1)",
            fill: false,
            tension: 0.3,
            borderWidth: 2,
          })),
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          animation: false,
          scales: {
            y: {
              type: "linear",
              title: { display: true, text: "USD ($)", font: { size: 14 } },
              min: 0,
              grid: { color: "#e0e0e0" },
              ticks: { font: { size: 12 } },
            },
            x: {
              title: { display: true, text: "Years", font: { size: 14 } },
              grid: { color: "#e0e0e0" },
              ticks: { font: { size: 12 } },
            },
          },
          plugins: {
            legend: {
              position: "bottom",
              labels: { font: { size: 14 }, padding: 20 },
            },
            tooltip: {
              callbacks: {
                label: (ctx: any) => {
                  const label = ctx.dataset.label || "";
                  const value = ctx.parsed.y ?? ctx.parsed;
                  return `${label}: $${Number(value).toLocaleString()}`;
                },
              },
            },
          },
        },
        plugins: [lineShadowPlugin, chartBackgroundPlugin],
      });
    }
  }

  // CO2 Savings Chart (Dual-Axis for better scaling)
  if (details.co2Savings) {
    const co2Canvas = document.createElement("canvas");
    co2Canvas.width = CHART_EXPORT_WIDTH;
    co2Canvas.height = CHART_EXPORT_HEIGHT;
    const co2Ctx = co2Canvas.getContext("2d");
    if (co2Ctx) {
      charts.co2Savings = new Chart(co2Ctx, {
        type: "bar",
        data: {
          labels: details.co2Savings.labels,
          datasets: details.co2Savings.datasets.map((dataset, index) => ({
            ...dataset,
            backgroundColor: ["#455354", "#758d8e"][index] || "#455354",
            borderColor: "#4b5b5d",
            borderWidth: 1,
            borderSkipped: false,
            barPercentage: 0.5,
            categoryPercentage: 0.5,
            borderRadius: 4,
            yAxisID: index === 0 ? "y" : "y1",
          })),
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          animation: false,
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: "CO₂ Offset (kg)",
                font: { size: 14 },
              },
              grid: { color: "#e0e0e0" },
            },
            y1: {
              beginAtZero: true,
              position: "right",
              title: {
                display: true,
                text: "Gasoline Saved (gallons)",
                font: { size: 14 },
              },
              grid: { drawOnChartArea: false },
            },
            x: {
              title: { display: true, text: "Years", font: { size: 14 } },
              grid: { color: "#e0e0e0" },
            },
          },
          plugins: {
            legend: {
              position: "bottom",
              labels: { font: { size: 14 }, padding: 20 },
            },
          },
        },
        plugins: [chartBackgroundPlugin],
      });
    }
  }

  // Wait for charts to render
  await new Promise((resolve) => setTimeout(resolve, 1000));

  return charts;
};

const splitTextIntoLines = (
  text: string,
  maxWidth: number,
  pdf: jsPDF,
  fontSize: number = 8
): string[] => {
  const lines: string[] = [];
  const words = text.split(" ");
  let currentLine = "";

  // Save current font size and set to calculation size
  const currentFontSize = pdf.getFontSize();
  pdf.setFontSize(fontSize);

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = pdf.getTextWidth(testLine);

    if (testWidth <= maxWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  // Restore original font size
  pdf.setFontSize(currentFontSize);

  return lines;
};

const toNumArr = (arr: any, len = 5) =>
  Array.isArray(arr) ? arr.map((x) => Number(x) || 0) : Array(len).fill(0);

const DEFAULT_YEAR_LABELS = [
  "Year 1",
  "Year 2",
  "Year 3",
  "Year 4",
  "Year 5",
];

const sanitizeDetails = (d: ReportDetails): ReportDetails => {
  const copy = { ...d };

  if (copy.co2Savings?.datasets?.length) {
    const labels =
      copy.co2Savings.labels && copy.co2Savings.labels.length
        ? copy.co2Savings.labels
        : DEFAULT_YEAR_LABELS;
    const labelCount = labels.length || 5;
    const safeCo2 = toNumArr(
      copy.co2Savings.datasets[0]?.data,
      labelCount
    );
    const safeGas = toNumArr(
      copy.co2Savings.datasets[1]?.data,
      labelCount
    );

    copy.co2Savings = {
      labels,
      datasets: [
        {
          label:
            copy.co2Savings.datasets[0]?.label || "CO₂ Offset (kg)",
          data: safeCo2,
        },
        {
          label:
            copy.co2Savings.datasets[1]?.label ||
            "Gasoline Saved (gallons)",
          data: safeGas,
        },
      ],
    };
  } else {
    delete copy.co2Savings;
  }

  return copy;
};

const NEGATIVE_VALUE_CATEGORIES = new Set<string>([
  "Install Cost",
  "Hardware Cost",
  "Network Cost",
  "Maintenance",
  "Utility Cost",
  "Utility Bill (Energy + Demand + Fixed)",
]);

const normalizeCategoryName = (category: string): string =>
  (category || "").replace(/^[\s-]+/, "").trim();

const addNegativePrefix = (
  value: string | number | null | undefined
): string => {
  if (value === null || value === undefined) return "-0";

  const sourceString =
    typeof value === "number" || typeof value === "bigint"
      ? value.toString()
      : `${value}`;

  const trimmed = sourceString.trim();

  if (!trimmed) return "-0";
  if (trimmed.startsWith("-")) return trimmed;
  if (trimmed.startsWith("$")) {
    return `-$${trimmed.slice(1)}`;
  }
  return `-${trimmed}`;
};

const formatFinancialTableValue = (
  category: string,
  value: string | number | null | undefined
): string => {
  const normalizedCategory = normalizeCategoryName(category);
  const isNegativeCategory = NEGATIVE_VALUE_CATEGORIES.has(
    normalizedCategory
  );

  if (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return isNegativeCategory ? addNegativePrefix(value) : "0";
  }

  if (!isNegativeCategory) {
    return typeof value === "string" ? value : String(value);
  }

  return addNegativePrefix(value);
};

export const downloadReport = async (
  // details = sanitizeDetails(ReportDetails),;
  details: ReportDetails,
  isWhiteLabel: boolean,
  whiteLabelConfig?: WhiteLabelConfig,
  returnBlob: boolean = false
): Promise<Blob | void> => {
  details = sanitizeDetails(details);
  console.log("White Label Config:", whiteLabelConfig);
  try {
    const pdf = new jsPDF({
      orientation: "p",
      unit: "mm",
      format: "a4",
      compress: true,
    });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pdfWidth - margin * 2;
    const chartAspectRatio = CHART_EXPORT_WIDTH / CHART_EXPORT_HEIGHT;
    const chartHeight = contentWidth / chartAspectRatio;
    const mapAspectRatio = MAP_EXPORT_WIDTH / MAP_EXPORT_HEIGHT;
    const mapHeight = contentWidth / mapAspectRatio;

    let currentY = margin;

    const addNewPage = () => {
      pdf.addPage();
      currentY = margin;
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.bgColor);
        pdf.setFillColor(r, g, b);
        pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
      }
    };

    const checkPageBreak = (requiredHeight: number) => {
      if (currentY + requiredHeight > pdfHeight - margin) {
        addNewPage();
      }
    };

    // Set background color for white label
    if (isWhiteLabel && whiteLabelConfig) {
      const { r, g, b } = hexToRgb(whiteLabelConfig.bgColor);
      pdf.setFillColor(r, g, b);
      pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
    }

    const charts = await generateCharts(details);
    const mapImageData = await createStaticMapImage(details.address);

    // Add user info for embedded reports (conditional)
    if (details.firstName) {
      checkPageBreak(15);
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "italic");
      pdf.text(
        `Customer Name: ${details.firstName} ${details.lastName}`,
        margin,
        currentY
      );
      currentY += 5;
      pdf.setFontSize(10);
      pdf.text(`Email: ${details.email}`, margin, currentY);
      currentY += 15;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(14);
    }

    // Add report title
    checkPageBreak(35);
    pdf.setFontSize(22);
    if (isWhiteLabel && whiteLabelConfig) {
      const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
      pdf.setTextColor(r, g, b);
    } else {
      pdf.setTextColor(75, 91, 93);
    }
    pdf.text(
      isWhiteLabel ? "Charging Station Report" : "L2 Charging Station Report",
      margin,
      currentY
    );
    currentY += 15;

    // Add logo
    try {
      let logoDataUrl: string;
      const logoSrc =
        isWhiteLabel && whiteLabelConfig?.brandLogo
          ? whiteLabelConfig.brandLogo
          : CompanyLogo;

      logoDataUrl = logoSrc;

      let imageFormat: "PNG" | "JPEG" = "PNG";
      if (typeof logoDataUrl === "string") {
        const lowerLogoSrc = logoDataUrl.toLowerCase();
        if (
          lowerLogoSrc.startsWith("data:image/jpeg") ||
          lowerLogoSrc.startsWith("data:image/jpg") ||
          lowerLogoSrc.endsWith(".jpg") ||
          lowerLogoSrc.endsWith(".jpeg")
        ) {
          imageFormat = "JPEG";
        }
      }

      if (
        typeof logoDataUrl === "string" &&
        logoDataUrl.startsWith("data:image")
      ) {
        const isPng = logoDataUrl.startsWith("data:image/png");
        logoDataUrl = await compressImageDataUrl(logoDataUrl, {
          maxWidth: 240,
          maxHeight: 120,
          quality: isPng ? undefined : 0.82,
          format: isPng ? "image/png" : "image/jpeg",
          backgroundColor: "#ffffff",
        });
        imageFormat = isPng ? "PNG" : "JPEG";
      }

      pdf.addImage(
        logoDataUrl,
        imageFormat,
        pdfWidth - margin - 50,
        currentY - 20,
        40,
        whiteLabelConfig ? 30 : 10,
        undefined,
        "FAST"
      );
      currentY += 10;
    } catch (error) {
      console.error("Failed to add logo:", error);
      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.text("[Company Logo]", pdfWidth - margin - 50, currentY);
      currentY += 10;
    }

    // Report Name
    pdf.setFontSize(14);
    if (isWhiteLabel && whiteLabelConfig) {
      const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
      pdf.setTextColor(r, g, b);
    } else {
      pdf.setTextColor(75, 91, 93);
    }
    pdf.text(details.reportName || "Charging Station", margin, currentY);
    currentY += 10;

    // Net Profit with Bullet Points
    checkPageBreak(60);
    pdf.setFillColor(isWhiteLabel ? 240 : 244, 240, isWhiteLabel ? 240 : 230);
    pdf.rect(margin, currentY, contentWidth, 40, "F");
    if (isWhiteLabel && whiteLabelConfig) {
      const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
      pdf.setTextColor(r, g, b);
    } else {
      pdf.setTextColor(75, 91, 93);
    }
    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.text(
      `5-Year Net Profit: $${details.netProfit.toLocaleString()}`,
      margin + 5,
      currentY + 10
    );
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text(
      "Projected total profit over five years",
      margin + 5,
      currentY + 15
    );

    // Calculate bullet points
    const bulletPoints: string[] = [];
    if (details.breakEvenCashFlow?.data) {
      const breakEvenPoint = details.breakEvenCashFlow.data.findIndex(
        (value) => value > 0
      );
      const breakEvenMonths = breakEvenPoint >= 0 ? breakEvenPoint * 12 : null;
      if (breakEvenMonths !== null) {
        bulletPoints.push(`${breakEvenMonths} Month Break Even Point`);
      }
    }
    if (details.co2Savings?.datasets) {
      const totalCo2Offset =
        details.co2Savings.datasets[0]?.data?.reduce(
          (sum: number, value: number) => sum + value,
          0
        ) || 0;
      const totalGasolineSaved =
        details.co2Savings.datasets[1]?.data?.reduce(
          (sum: number, value: number) => sum + value,
          0
        ) || 0;
      if (totalCo2Offset > 0) {
        bulletPoints.push(`${totalCo2Offset.toLocaleString()} kg CO2 Offset`);
      }
      if (totalGasolineSaved > 0) {
        bulletPoints.push(
          `${totalGasolineSaved.toLocaleString()} gallons of Gasoline Saved`
        );
      }
    }

    // Add bullet points
    pdf.setFontSize(10);
    bulletPoints.forEach((point, index) => {
      pdf.text("•", margin + 5, currentY + 25 + index * 5);
      pdf.text(point, margin + 10, currentY + 25 + index * 5);
    });
    currentY += 40 + bulletPoints.length * 5;

    // Financial Table
    if (details.financialTable && details.financialTable.length > 0) {
      checkPageBreak(30);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.text("Financial Breakdown", margin, currentY);
      currentY += 10;

      // Draw table border
      pdf.setLineWidth(0.5);
      pdf.setDrawColor(75, 91, 93);
      pdf.rect(
        margin,
        currentY,
        contentWidth,
        10 + details.financialTable.length * 10 + 10
      );

      // Table header
      pdf.setFillColor(245, 240, 231);
      pdf.rect(margin, currentY, contentWidth, 10, "F");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.setFontSize(11);
      pdf.setFont("helvetica", "bold");
      pdf.text("Category", margin + 5, currentY + 7);
      pdf.text("Amount/Value", margin + contentWidth / 2 + 5, currentY + 7);
      currentY += 10;

      // Table rows
      details.financialTable.forEach((item, index) => {
        checkPageBreak(10);
        pdf.setFillColor(
          index % 2 === 0 ? 245 : 237,
          index % 2 === 0 ? 240 : 231,
          index % 2 === 0 ? 231 : 219
        );
        pdf.rect(margin, currentY, contentWidth, 10, "F");
        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(75, 91, 93);
        }
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.text(item.category, margin + 5, currentY + 7);
        pdf.setFont("helvetica", "normal");
        const formattedValue = formatFinancialTableValue(
          item.category,
          item.value
        );
        pdf.text(formattedValue, margin + contentWidth / 2 + 5, currentY + 7);
        currentY += 10;
      });

      // // Net Profit row
      // checkPageBreak(10);
      // pdf.setFillColor(245, 240, 231);
      // pdf.rect(margin, currentY, contentWidth, 10, "F");
      // if (isWhiteLabel && whiteLabelConfig) {
      //   const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
      //   pdf.setTextColor(r, g, b);
      // } else {
      //   pdf.setTextColor(75, 91, 93);
      // }
      // pdf.setFontSize(11);
      // pdf.setFont("helvetica", "bold");
      // pdf.text("Net Profit", margin + 5, currentY + 7);
      // pdf.text(
      //   `$${details.netProfit.toLocaleString()}`,
      //   margin + contentWidth / 2 + 5,
      //   currentY + 7
      // );
      currentY += 15;
    }

    const hasRealUtilityData = (details: ReportDetails): boolean => {
      const u = details.utilityDetails;
      const e = details.energyCosts;
    
      const hasValidUtilityInfo = u && (
        (u.state && u.state.trim() !== "--") ||
        (u.zipCode && u.zipCode.trim() !== "--") ||
        (u.utilityName && u.utilityName.trim() !== "--") ||
        u.totalCost > 0
      );
    
      const hasNonZeroCosts = e && [
        ...toNumArr(e.total),
        ...toNumArr(e.energy),
        ...toNumArr(e.demand),
        ...toNumArr(e.incrementalEnergy),
        ...toNumArr(e.incrementalDemand),
      ].some(v => v > 0.01); 
    
      return (hasValidUtilityInfo || hasNonZeroCosts) ?? false;
    };
    
    const shouldRenderUtilitySection = hasRealUtilityData(details);

    if (shouldRenderUtilitySection) {
      checkPageBreak(30);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.text("Utility Details & Energy Costs", margin, currentY);
      currentY += 10;

      if (details.utilityDetails) {
        pdf.setLineWidth(0.5);
        pdf.setDrawColor(75, 91, 93);
        pdf.rect(margin, currentY, contentWidth, 20);

        pdf.setFillColor(245, 240, 231);
        pdf.rect(margin, currentY, contentWidth, 10, "F");
        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(75, 91, 93);
        }
        pdf.setFontSize(11);
        pdf.setFont("helvetica", "bold");
        const utilityColumns = [
          "State",
          "Zip Code",
          "Utility Name",
          "Utility ID",
          "Schedule ID",
          "Full Utility Bill",
        ];
        const colWidth = contentWidth / 6;
        utilityColumns.forEach((col, idx) => {
          pdf.text(col, margin + idx * colWidth + 5, currentY + 7);
        });
        currentY += 10;

        checkPageBreak(10);
        pdf.setFillColor(237, 231, 219);
        pdf.rect(margin, currentY, contentWidth, 10, "F");
        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(75, 91, 93);
        }
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");

        const {
          state = "--",
          zipCode = "--",
          utilityName = "--",
          utilityId = "--",
          scheduleId = "--",
          totalCost = 0,
        } = details.utilityDetails;

        pdf.text(state, margin + 5, currentY + 7);
        pdf.text(zipCode, margin + colWidth + 5, currentY + 7);

        const utilityNameLines = splitTextIntoLines(
          utilityName,
          colWidth - 10,
          pdf,
          8
        );
        utilityNameLines.forEach((line, lineIndex) => {
          pdf.text(line, margin + 2 * colWidth + 5, currentY + 3 + lineIndex * 4);
        });
        const utilityNameHeight = Math.max(10, utilityNameLines.length * 4);

        pdf.text(utilityId, margin + 3 * colWidth + 5, currentY + 7);
        pdf.text(scheduleId, margin + 4 * colWidth + 5, currentY + 7);
        pdf.text(
          `$${Number(totalCost).toLocaleString()}`,
          margin + 5 * colWidth + 5,
          currentY + 7
        );

        currentY += Math.max(10, utilityNameHeight);
        currentY += 5;
      }

      if (details.energyCosts) {
        checkPageBreak(30);
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(75, 91, 93);
        }

        pdf.setLineWidth(0.5);
        pdf.setDrawColor(75, 91, 93);
        pdf.rect(margin, currentY, contentWidth, 10 + 9 * 10);

        pdf.setFillColor(245, 240, 231);
        pdf.rect(margin, currentY, contentWidth, 10, "F");
        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(75, 91, 93);
        }
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        const energyColumns = [
          "Category",
          "Year 1",
          "Year 2",
          "Year 3",
          "Year 4",
          "Year 5",
        ];
        const energyColWidth = contentWidth / 6;
        energyColumns.forEach((col, idx) => {
          pdf.text(col, margin + idx * energyColWidth + 3, currentY + 7);
        });
        currentY += 10;

        const energyRows = [
          {
            category: "Energy",
            data: toNumArr(details.energyCosts.energy),
          },
          {
            category: "Energy Time",
            data: toNumArr(details.energyCosts.energyTime),
          },
          {
            category: "Incremental Energy",
            data: toNumArr(details.energyCosts.incrementalEnergy),
          },
          {
            category: "Demand",
            data: toNumArr(details.energyCosts.demand),
          },
          {
            category: "Demand Time",
            data: toNumArr(details.energyCosts.demandTime),
          },
          {
            category: "Incremental Demand",
            data: toNumArr(details.energyCosts.incrementalDemand),
          },
          {
            category: "Service Charge",
            data: toNumArr(details.energyCosts.serviceCharge),
          },
          {
            category: "Other Charges",
            data: toNumArr(details.energyCosts.otherCharges),
          },
          {
            category: "Total",
            data: toNumArr(details.energyCosts.total),
          },
        ];

        energyRows.forEach((row, index) => {
          checkPageBreak(10);
          pdf.setFillColor(
            index % 2 === 0 ? 245 : 237,
            index % 2 === 0 ? 240 : 231,
            index % 2 === 0 ? 231 : 219
          );
          pdf.rect(margin, currentY, contentWidth, 10, "F");
          if (isWhiteLabel && whiteLabelConfig) {
            const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
            pdf.setTextColor(r, g, b);
          } else {
            pdf.setTextColor(75, 91, 93);
          }
          pdf.setFontSize(9);
          pdf.setFont("helvetica", "bold");
          pdf.text(row.category, margin + 3, currentY + 7);
          pdf.setFont("helvetica", "normal");
          row.data.forEach((value, yearIdx) => {
            pdf.text(
              `$${value.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
              margin + (yearIdx + 1) * energyColWidth + 5,
              currentY + 7
            );
          });
          currentY += 10;
        });
        currentY += 15;
      }
    }

    // Grant Cost Details Table (only if not excluded and has data)
    if (
      !details.excludeIncentives &&
      details.grantCostDetails &&
      details.grantCostDetails.length > 0
    ) {
      checkPageBreak(40);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.text("Grant Cost Details", margin, currentY);
      currentY += 10;

      // Table Header
      pdf.setFillColor(230, 230, 230);
      pdf.rect(margin, currentY, contentWidth, 12, "F");
      pdf.setLineWidth(0.5);
      pdf.setDrawColor(75, 91, 93);
      pdf.rect(margin, currentY, contentWidth, 12);

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      const columns = [
        "Program Name",
        "Credit Amount",
        "Applies To",
        "Eligibility",
        "Valid Until",
      ];
      const colWidths = [0.3, 0.15, 0.2, 0.2, 0.15].map(
        (ratio) => contentWidth * ratio
      );
      columns.forEach((col, idx) => {
        const xPos =
          margin + colWidths.slice(0, idx).reduce((sum, w) => sum + w, 0) + 5;
        pdf.text(col, xPos, currentY + 8);
      });
      currentY += 12;

      // Process each grant
      details.grantCostDetails.forEach((grant, grantIndex) => {
        // Calculate heights for dynamic content
        const baseRowHeight = 15;
        const descriptionLines = splitTextIntoLines(
          grant.description,
          contentWidth - 20,
          pdf,
          8
        );
        const descriptionHeight = descriptionLines.length * 6;
        let amountDetailsHeight = 0;
        let amountLines: string[] = [];
        if (grant.fullAmountDetails) {
          amountLines = splitTextIntoLines(
            grant.fullAmountDetails,
            contentWidth - 20,
            pdf,
            8
          );
          amountDetailsHeight = amountLines.length * 6 + 10;
        }
        const actionLinkHeight = 10;
        const totalGrantHeight =
          baseRowHeight +
          descriptionHeight +
          amountDetailsHeight +
          actionLinkHeight +
          15;

        checkPageBreak(totalGrantHeight);

        // Main grant row
        pdf.setFillColor(
          grantIndex % 2 === 0 ? 245 : 237,
          grantIndex % 2 === 0 ? 240 : 231,
          grantIndex % 2 === 0 ? 231 : 219
        );
        pdf.rect(margin, currentY, contentWidth, baseRowHeight, "F");
        pdf.setLineWidth(0.3);
        pdf.setDrawColor(75, 91, 93);
        pdf.rect(margin, currentY, contentWidth, totalGrantHeight);

        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(75, 91, 93);
        }

        // Program Name
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "bold");
        const programNameLines = splitTextIntoLines(
          grant.programName,
          colWidths[0] - 10,
          pdf,
          9
        );
        programNameLines.forEach((line, lineIndex) => {
          pdf.text(line, margin + 5, currentY + 8 + lineIndex * 6);
        });

        // Credit Amount (highlighted)
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(46, 125, 50);
        const creditAmountLines = splitTextIntoLines(
          grant.creditAmount,
          colWidths[1] - 10,
          pdf,
          9
        );
        creditAmountLines.forEach((line, lineIndex) => {
          pdf.text(
            line,
            margin + colWidths[0] + 5,
            currentY + 8 + lineIndex * 6
          );
        });

        // Reset text color
        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(75, 91, 93);
        }
        pdf.setFont("helvetica", "normal");

        // Applies To
        const appliesToLines = splitTextIntoLines(
          grant.appliesTo,
          colWidths[2] - 10,
          pdf,
          9
        );
        appliesToLines.forEach((line, lineIndex) => {
          pdf.text(
            line,
            margin + colWidths.slice(0, 2).reduce((sum, w) => sum + w, 0) + 5,
            currentY + 8 + lineIndex * 6 // Increased line spacing to 6
          );
        });

        // Eligibility
        const eligibilityLines = splitTextIntoLines(
          grant.eligibility,
          colWidths[3] - 10,
          pdf,
          9
        );
        eligibilityLines.forEach((line, lineIndex) => {
          pdf.text(
            line,
            margin + colWidths.slice(0, 3).reduce((sum, w) => sum + w, 0) + 5,
            currentY + 8 + lineIndex * 6 // Increased line spacing to 6
          );
        });

        // Valid Until
        pdf.text(
          grant.validUntil,
          margin + colWidths.slice(0, 4).reduce((sum, w) => sum + w, 0) + 5,
          currentY + 8
        );

        currentY += baseRowHeight;

        // Description section
        pdf.setFillColor(248, 248, 248);
        pdf.rect(margin, currentY, contentWidth, descriptionHeight + 8, "F");
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "bold");
        pdf.text("Description:", margin + 5, currentY + 5);
        pdf.setFont("helvetica", "normal");
        descriptionLines.forEach((line, lineIndex) => {
          pdf.text(line, margin + 5, currentY + 10 + lineIndex * 6); // Increased line spacing to 6
        });
        currentY += descriptionHeight + 8;

        // Amount Details section (if available)
        if (grant.fullAmountDetails) {
          pdf.setFillColor(242, 242, 242);
          pdf.rect(
            margin,
            currentY,
            contentWidth,
            amountDetailsHeight + 8, // Use updated amountDetailsHeight
            "F"
          );
          pdf.setFontSize(8);
          pdf.setFont("helvetica", "bold");
          pdf.text("Amount Details:", margin + 5, currentY + 5);
          pdf.setFont("helvetica", "normal");
          amountLines.forEach((line, lineIndex) => {
            pdf.text(line, margin + 5, currentY + 10 + lineIndex * 6); // Increased line spacing to 6
          });
          currentY += amountDetailsHeight + 8;
        }

        // Action Link section
        pdf.setFillColor(250, 250, 250);
        pdf.rect(margin, currentY, contentWidth, actionLinkHeight, "F");
        pdf.setFontSize(8);
        pdf.setTextColor(0, 0, 255);
        pdf.textWithLink("Click To Program URL", margin + 5, currentY + 7, {
          url: grant.actionLink,
        });
        currentY += actionLinkHeight + 5;
      });
      currentY += 10;
    }

    // Location Map
    checkPageBreak(mapHeight + 20);
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    if (isWhiteLabel && whiteLabelConfig) {
      const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
      pdf.setTextColor(r, g, b);
    } else {
      pdf.setTextColor(75, 91, 93);
    }
    pdf.text("Location Map", margin, currentY);
    currentY += 10;

    if (mapImageData && mapImageData.startsWith("data:image")) {
      pdf.addImage(
        mapImageData,
        "JPEG",
        margin,
        currentY,
        contentWidth,
        mapHeight,
        undefined,
        "FAST"
      );
      currentY += mapHeight + 15;
    } else {
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.text(
        `Address: ${details.address || "Charging Station Location"}`,
        margin,
        currentY
      );
      currentY += 8;

      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, currentY, contentWidth, 40, "F");
      pdf.setFillColor(200, 200, 200);
      pdf.rect(margin + 10, currentY + 10, 20, 20, "F");
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(10);
      pdf.text("📍", margin + 15, currentY + 25);
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.setFontSize(9);
      pdf.text("Charging Station Location", margin + 35, currentY + 20);
      pdf.text(
        "Map visualization could not be captured",
        margin + 35,
        currentY + 30
      );
      currentY += 50;
    }

    // Tax Benefits Details Table (if applicable)
    if (details.taxBenefits?.applyTaxBenefits) {
      checkPageBreak(40);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.text("Tax Benefits Details", margin, currentY);
      currentY += 10;

      // Draw table border
      pdf.setLineWidth(0.5);
      pdf.setDrawColor(75, 91, 93);
      pdf.rect(margin, currentY, contentWidth, 10 + 6 * 10);

      // Table header
      pdf.setFillColor(245, 240, 231);
      pdf.rect(margin, currentY, contentWidth, 10, "F");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }
      pdf.setFontSize(11);
      pdf.setFont("helvetica", "bold");
      pdf.text("Category", margin + 5, currentY + 7);
      pdf.text("Value", margin + contentWidth / 2 + 5, currentY + 7);
      currentY += 10;

      // Tax benefits data rows
      const taxBenefitsRows = [
        {
          category: "Business Tax Rate",
          value: `${details.taxBenefits.businessTaxRate || 0}%`,
        },
        {
          category: "Section 179 Limit",
          value: `$${(
            details.taxBenefits.section179Limit || 0
          ).toLocaleString()}`,
        },
        {
          category: "Bonus Depreciation %",
          value: `${details.taxBenefits.bonusDepreciationPct || 0}%`,
        },
        {
          category: "Qualifying Spend",
          value: `$${(
            details.taxBenefits.qualifyingSpend || 0
          ).toLocaleString()}`,
        },
        {
          category: "Estimated Tax Savings",
          value: `$${Math.round(
            details.taxBenefits.taxSavings || 0
          ).toLocaleString()}`,
        },
        {
          category: "Upfront Cost After Tax",
          value: `$${Math.round(
            details.taxBenefits.upfrontAfterTax || 0
          ).toLocaleString()}`,
        },
      ];

      taxBenefitsRows.forEach((row, index) => {
        checkPageBreak(10);
        pdf.setFillColor(
          index % 2 === 0 ? 245 : 237,
          index % 2 === 0 ? 240 : 231,
          index % 2 === 0 ? 231 : 219
        );
        pdf.rect(margin, currentY, contentWidth, 10, "F");
        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(75, 91, 93);
        }
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.text(row.category, margin + 5, currentY + 7);

        // Highlight tax savings row
        if (row.category === "Estimated Tax Savings") {
          pdf.setTextColor(46, 125, 50);
        }

        pdf.setFont("helvetica", "normal");
        pdf.text(row.value, margin + contentWidth / 2 + 5, currentY + 7);

        // Reset text color
        if (isWhiteLabel && whiteLabelConfig) {
          const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
          pdf.setTextColor(r, g, b);
        } else {
          pdf.setTextColor(75, 91, 93);
        }

        currentY += 10;
      });

      currentY += 15;

      // Tax Disclaimer
      checkPageBreak(25);
      pdf.setFillColor(255, 243, 205);
      pdf.rect(margin, currentY, contentWidth, 20, "F");
      pdf.setLineWidth(0.3);
      pdf.setDrawColor(255, 193, 7);
      pdf.rect(margin, currentY, contentWidth, 20);

      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(133, 100, 4);
      }

      const disclaimerText =
        "Tax disclaimer: Estimates are illustrative only and do not constitute tax advice. Section 179 and 168(k) eligibility and limits vary by entity and state. Consult a qualified tax professional to confirm treatment for your situation.";
      const disclaimerLines = splitTextIntoLines(
        disclaimerText,
        contentWidth - 10,
        pdf,
        8
      );

      disclaimerLines.forEach((line, lineIndex) => {
        pdf.text(line, margin + 5, currentY + 8 + lineIndex * 4);
      });

      currentY += 25;
    }

    currentY += 15;

    // Charts
    const chartEntries = Object.entries(charts).filter(([, chart]) => chart);
    for (let i = 0; i < chartEntries.length; i++) {
      checkPageBreak(chartHeight + 25);

      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      if (isWhiteLabel && whiteLabelConfig) {
        const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
        pdf.setTextColor(r, g, b);
      } else {
        pdf.setTextColor(75, 91, 93);
      }

      const chartName = chartEntries[i][0];
      const chartTitle =
        chartName === "breakEven"
          ? "Break Even/Cash Flow Analysis"
          : chartName === "revenueCost"
          ? "5-Year Revenue & Cost Breakdown"
          : "CO2 Savings & Environmental Impact";

      pdf.text(chartTitle, margin, currentY);
      currentY += 8;

      const chart = chartEntries[i][1] as Chart;
      const chartCanvas = chart.canvas as HTMLCanvasElement;
      const chartImageData = chartCanvas.toDataURL(
        "image/jpeg",
        PDF_IMAGE_QUALITY
      );

      pdf.addImage(
        chartImageData,
        "JPEG",
        margin,
        currentY,
        contentWidth,
        chartHeight,
        undefined,
        "FAST"
      );
      currentY += chartHeight + 20;
    }

    // Add Terms & Conditions and Privacy Policy
    if (isWhiteLabel && whiteLabelConfig) {
      checkPageBreak(30);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      const { r, g, b } = hexToRgb(whiteLabelConfig.textColor);
      pdf.setTextColor(r, g, b);
      pdf.text("Additional Information", margin, currentY);
      currentY += 10;

      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");

      if (whiteLabelConfig.termsOfServiceURL) {
        checkPageBreak(10);
        pdf.text("Terms & Conditions:", margin, currentY);
        pdf.setTextColor(0, 0, 255);
        pdf.textWithLink(
          whiteLabelConfig.termsOfServiceURL,
          margin + 45,
          currentY + 0,
          {
            url: whiteLabelConfig.termsOfServiceURL,
          }
        );
        currentY += 15;
        pdf.setTextColor(r, g, b);
      }

      if (whiteLabelConfig.privacyPolicyURL) {
        checkPageBreak(10);
        pdf.text("Privacy Policy:", margin, currentY);
        pdf.setTextColor(0, 0, 255);
        pdf.textWithLink(
          whiteLabelConfig.privacyPolicyURL,
          margin + 35,
          currentY + 0,
          {
            url: whiteLabelConfig.privacyPolicyURL,
          }
        );
        currentY += 15;
        pdf.setTextColor(r, g, b);
      }
    }

    // Destroy charts after rendering
    Object.values(charts).forEach((chart) => chart?.destroy());

    // Save or return the PDF
    const filenameBase = (details.reportName || "Charging_Station").replace(
      /[^a-z0-9]+/gi,
      "_"
    );
    const filename = `${filenameBase}_${isWhiteLabel ? "WL" : "Report"}_${
      new Date().toISOString().split("T")[0]
    }.pdf`;

    if (returnBlob) {
      const pdfAny = pdf as unknown as {
        output: (
          type: string,
          options?: { compress?: boolean }
        ) => ArrayBuffer | string;
      };
      const arrayBuffer = pdfAny.output("arraybuffer", { compress: true }) as ArrayBuffer;
      return new Blob([arrayBuffer], { type: "application/pdf" });
    }
    pdf.save(filename);
  } catch (err) {
    console.error("PDF generation failed", err);
    throw err;
  }
};
